import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import BrandLogo from "@/components/BrandLogo";
import { motion } from "framer-motion";
import { HandHeart, ShieldCheck, HeartStraight, ArrowLeft } from "@phosphor-icons/react";

const paragraphs = [
  "Some of life's most important moments are built on trust.",
  "A friend who stands by you without hesitation. A family member who believes in you when no one else does. A promise made with sincerity. A helping hand offered without expecting anything in return.",
  "Trust is one of humanity's greatest strengths. It builds families, friendships, businesses, and communities. Yet even the strongest relationships can be tested when expectations remain unspoken or memories begin to fade.",
  "We believe that trust should never have to depend on memory, uncertainty, or circumstance. It deserves clarity. It deserves respect. Above all, it deserves protection.",
  "LeLe was born from a simple belief: technology should not replace trust — it should strengthen it.",
  "Everything we build is guided by one purpose: helping people preserve what matters most — not just what they own, but the relationships they value.",
  "Because when trust is protected, relationships last longer, promises carry greater meaning, and people move forward with confidence.",
];

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <main className="flex-1 mx-auto max-w-3xl px-5 md:px-8 py-10 md:py-16" data-testid="about-root">
        <Link
          to="/"
          className="text-sm text-neutral-500 hover:text-neutral-200 flex items-center gap-1.5 mb-8"
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <BrandLogo size={56} />
          <div className="text-xs uppercase tracking-[0.35em] text-indigo-300 mt-6 mb-3">
            About LeLe
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] tracking-tight">
            Some of life's most important moments
            <span className="text-indigo-400"> are built on trust.</span>
          </h1>
        </motion.div>

        <div className="mt-10 space-y-6 text-neutral-300 text-base md:text-[17px] leading-[1.85]">
          {paragraphs.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className={i === 0 ? "text-neutral-100 text-lg md:text-xl font-light" : ""}
            >
              {p}
            </motion.p>
          ))}
        </div>

        <div className="mt-16 lele-card p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-indigo-500/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-16 h-40 w-40 rounded-full bg-fuchsia-500/10 blur-3xl" />
          <div className="relative">
            <HandHeart size={36} weight="duotone" className="text-indigo-400 mb-4" />
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
              Our promise
            </div>
            <h2 className="font-heading text-3xl md:text-4xl mb-4">
              LeLe — Protect Every Rupee.
              <br />
              <span className="text-indigo-300">Preserve Every Relationship.</span>
            </h2>
            <div className="mt-6 flex flex-col sm:flex-row gap-5 pt-6 border-t border-white/5">
              <div className="flex items-start gap-3 flex-1">
                <ShieldCheck size={20} weight="duotone" className="text-indigo-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-neutral-100">Legally documented</div>
                  <div className="text-xs text-neutral-500 mt-1 leading-relaxed">
                    Every loan captured as an agreement + promissory note, with the borrower's timestamped consent.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 flex-1">
                <HeartStraight size={20} weight="duotone" className="text-fuchsia-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-neutral-100">Relationship-first</div>
                  <div className="text-xs text-neutral-500 mt-1 leading-relaxed">
                    Gentle reminders, clear terms, dignified conversations — never harsh recovery tactics.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
